import { Licences } from '@seek/profile-read-ui';
import { useTranslations } from '@vocab/react';
import { Stack } from 'braid-design-system';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Card } from 'src/components/Card/Card';
import { CardAction } from 'src/components/Card/CardAction/CardAction';
import { FormDrawer } from 'src/components/FormDrawer/FormDrawer';
import { LoadingCardItem } from 'src/components/LoadingBlock/LoadingBlock';
import { LICENCES } from 'src/components/Router';
import { updateRoute } from 'src/components/Router/actionCreators';
import { EDIT } from 'src/consts';
import { certsyLinkClicked } from 'src/modules/analytics/certsy/actionCreators';
import type { CertsyLinkPosition } from 'src/modules/analytics/certsy/eventNames';
import { useTypedSelector } from 'src/modules/hooks/useTypedSelector';
import {
  getFailedActionsForParent,
  retryParent,
} from 'src/modules/redux-blend';
import { LICENCES_FORM } from 'src/reducers/forms';

import { EditLicences } from '../EditLicences/EditLicences';
import { useLicences } from '../data';
import type { GraphqlLicence } from '../types';
import { graphQLLicenceToUILicence } from '../utils';

import translations from './.vocab';

export const ReadLicences = () => {
  const { licences, loading, error } = useLicences();

  const { t } = useTranslations(translations);
  const [drawerTitle, setDrawerTitle] = useState<string>('');

  const blend = useTypedSelector((state) => state.blend);
  const page = useTypedSelector((state) => state.router.page);
  const mode = useTypedSelector((store) => store.router[LICENCES].mode);
  const licenceId = useTypedSelector(
    (store) => store.router[LICENCES].params.id,
  );
  const isCreateResumePage = page === 'create-resume-page';

  const isEmpty = licences.length === 0;

  const dispatch = useDispatch();

  useEffect(() => {
    if (mode === 'EDIT') {
      if (licenceId) {
        setDrawerTitle(t('Edit licence or certification'));
      } else {
        setDrawerTitle(t('Add licence or certification'));
      }
    }
  }, [mode, t, licenceId]);

  const editLicence = (id: string) => {
    dispatch(
      updateRoute({
        to: LICENCES,
        mode: EDIT,
        params: { id },
        analytics: {
          currentPageSectionState: 'edit',
        },
      }),
    );
  };

  const showRetry = getFailedActionsForParent({ blend }, LICENCES).length > 0;
  const handleRetry = () => dispatch(retryParent(LICENCES));

  const cardAction = (
    <CardAction
      to={LICENCES}
      mode={EDIT}
      actionText={t('Add licence or certification')}
      data={{ automation: 'licence-add' }}
      isEmpty={isEmpty}
      analytics={{
        currentPageSectionState: 'add',
      }}
    />
  );

  const onCertsyLinkClick = useCallback(
    (linkPosition: CertsyLinkPosition, licence: GraphqlLicence) => () => {
      if (licence?.credentialType) {
        dispatch(
          certsyLinkClicked({
            linkAction: 'verify',
            linkPosition,
            ...(licence.verificationUrl
              ? { linkUrl: licence.verificationUrl }
              : {}),
            credentialType: licence.credentialType,
            currentPageSection: 'licence',
          }),
        );
      }
    },
    [dispatch],
  );

  const description =
    drawerTitle === t('Edit licence or certification')
      ? undefined
      : t('Showcase your licences...');
  const isLoading = Boolean(loading || error);

  return (
    <>
      <Card
        name={LICENCES}
        title={t('Licences & certifications')}
        emptyCardText={t('Showcase your professional credentials...')}
        isEmpty={isEmpty}
        retryAction={showRetry ? handleRetry : undefined}
        cardAction={cardAction}
        showOptionalBadge={isCreateResumePage}
        loading={isLoading}
        loader={
          <Stack space="small">
            <LoadingCardItem />
            <LoadingCardItem />
          </Stack>
        }
      >
        <Licences
          displayVariant="card"
          licences={licences.map((licence) => ({
            ...graphQLLicenceToUILicence(licence),
            editLicence,
            onVerifyClick: onCertsyLinkClick('main card', licence),
            onCertsyClick: onCertsyLinkClick('pop up', licence),
            showCertsyFunctionality: !isCreateResumePage,
          }))}
        />
      </Card>
      <FormDrawer
        title={drawerTitle}
        description={description}
        component={EditLicences}
        card={LICENCES}
        formName={LICENCES_FORM}
      />
    </>
  );
};
